import type { ThemeId, TimeOfDay, AvatarConfig, WordDTO, ChallengeKind } from "@shared/types";
import type { UpgradeLevels } from "@shared/upgrades";
import type { MovementMode } from "../local/gameSettings";

export interface SpawnedCoin {
  id: number;
  z: number;
  lane: number;
  collected: boolean;
}

export interface WordGate {
  id: number;
  z: number;
  word: WordDTO;
  triggered: boolean;
}

export interface RunState {
  hearts: number;
  stumbles: number;
  coins: number;
  score: number;
  startedAt: number;
  finishedAt: number | null;
  distance: number;
  paused: boolean;
  speed: number;
  invulnerableUntil: number;
}

export interface GameConfig {
  lessonId: string;
  words: WordDTO[];
  theme: ThemeId;
  timeOfDay: TimeOfDay;
  questionsCount: number;
  objectsCount: number;
  coinsCount: number;
  /** Coins awarded for one physical coin pickup. Scales by level. */
  coinPickupValue?: number;
  /** Coins awarded for a correct word gate answer before upgrades. Scales by level. */
  quizCorrectCoins?: number;
  courseLength: number; // world units
  seed: number;
  avatar: AvatarConfig;
  /** Restrict gates to these challenge kinds. Null/empty = full rotation. */
  challengeKinds?: ChallengeKind[] | null;
  /** Persistent upgrade levels purchased by this student. */
  upgrades?: UpgradeLevels;
  /** Fixed run speed in m/s. Overrides the default ramp. */
  runSpeed?: number;
  /** Free movement is the default Fall Guys-style mode; lanes remain for beginners. */
  movementMode?: MovementMode;
  /** Gamepad/analog movement sensitivity. Keyboard stays digital. */
  movementSensitivity?: number;
  /** Right-stick camera sensitivity. */
  cameraSensitivity?: number;
  /** Controller stick deadzone. */
  controllerDeadzone?: number;
  /** Accessibility: when true the game auto-runs forward and auto-jumps over
   *  approaching obstacles. Player input only controls left/right steering.
   *  Aimed at very young kids so they can still finish a level. */
  assistMode?: boolean;
  /** Multiplayer: opponents to render as ghosts on the same path. */
  remotePlayers?: { id: string; displayName: string; avatarConfig: AvatarConfig; slotIdx: number }[];
  /** Multiplayer: after local death, follow a remaining live opponent until results. */
  spectateAfterDeath?: boolean;
  /** Multiplayer: my own slot index, used to compute lateral offset. */
  mySlotIdx?: number;
  /** Course shape. "hit-parade" = straight flat→uphill→flat with bouncing balls (level 1).
   *  "ice-crack" = straight flat with cracking ice tile fields between checkpoint gates (level 2).
   *  "door-dash" = pink/purple straight track with random open/blocked door rows between
   *  checkpoint gates and big spinning bars (level 3).
   *  "rolling-tubes" = 5 platforms (each with a question gate) connected by slowly
   *  spinning horizontal tubes that the player must run across (level 4).
   *  "circle-spinner" = 5 round spinner platforms with rotating bars, connected by
   *  short corridors with word checkpoint gates (level 5). */
  courseMode?: "default" | "hit-parade" | "ice-crack" | "door-dash" | "rolling-tubes" | "circle-spinner" | "maze-rush" | "space-ship" | "supermarket-sweep" | "block-climb" | "river-boat" | "double-spinner" | "train-roof";
  /** Date.now() reference shared across clients in multiplayer so time-based
   *  animations (moving platforms, cannon firing, etc.) line up exactly on
   *  every device. Defaults to Date.now() at game start in solo mode. */
  clockOrigin?: number;
  /** SAFETY NET for accidental Game remounts mid-race (multiplayer race
   *  config can re-identity due to roster broadcasts). If supplied, the
   *  Game constructor will resume from this s-distance + last-known
   *  checkpoint instead of starting at 0. Ignored when 0 / undefined. */
  resumeFromS?: number;
  /** Optional per-level overrides for floaty levels (e.g. space). */
  gravityOverride?: number;
  airControl?: number;
  /** Last gate index the player crossed before the remount, so the fall-
   *  respawn target is preserved. */
  resumeLastGateS?: number;
  /** Mount the game in paused state (for showing a countdown overlay first). */
  startPaused?: boolean;
  /** When mounted paused at the start, preview the track from finish to start. */
  startCameraFlyover?: boolean;
}

export interface RunOutcome {
  score: number;
  timeMs: number;
  coinsEarned: number; // raw coins picked up + answer rewards (excludes bonuses below)
  hearts: number;
  stumbles: number;
  wordOutcomes: { wordId: string; correct: boolean }[];
  finished: boolean; // reached the end vs. failed the learning checks
}

export interface BonusBreakdown {
  baseCoins: number;
  heartsBonus: number;
  timeBonus: number;
  flawlessBonus: number;
  totalCoins: number;
  totalScore: number;
  flawless: boolean;
}

export function computeBonuses(o: RunOutcome): BonusBreakdown {
  const total = o.wordOutcomes.length;
  const correct = o.wordOutcomes.filter((w) => w.correct).length;
  const allCorrect = total > 0 && correct === total;
  const heartsBonus = o.finished ? o.hearts * 25 : 0;
  // Time bonus: faster than 60s = up to +50, scaled.
  const seconds = o.timeMs / 1000;
  const timeBonus = o.finished ? Math.max(0, Math.round((90 - seconds) * 1.5)) : 0;
  const flawless = o.finished && allCorrect && o.hearts === 3;
  const flawlessBonus = flawless ? 100 : 0;
  return {
    baseCoins: o.coinsEarned,
    heartsBonus,
    timeBonus,
    flawlessBonus,
    totalCoins: o.coinsEarned + heartsBonus + timeBonus + flawlessBonus,
    totalScore: o.score + heartsBonus * 5 + timeBonus * 5 + flawlessBonus * 5,
    flawless,
  };
}

export function rewardSettingsForLesson(orderIndex: number, baseCoinsCount: number) {
  const level = Math.max(1, orderIndex);
  return {
    coinsCount: baseCoinsCount + (level - 1) * 8,
    coinPickupValue: 1 + Math.floor((level - 1) / 3),
    quizCorrectCoins: 5 + Math.floor((level - 1) * 1.5),
  };
}

export type GameEvent =
  | { type: "challenge"; word: WordDTO; resolve: (correct: boolean) => void }
  | { type: "hit" }
  | { type: "coin"; value: number }
  | { type: "finish"; outcome: RunOutcome }
  | { type: "death"; outcome: RunOutcome }
  | { type: "state"; state: RunState };

export type GameEventHandler = (e: GameEvent) => void;
